const KIND_ORDER = { percent: 0, fixed: 1, none: 2 }

/**
 * Whether a quote item is a part library entry whose fixed price still applies. The API
 * never adjusts these items. Same rule as the item editor (`ItemPrices.jsx`): the price
 * expires the day after `fixedPriceEndTime`, and no end time means it never expires.
 *
 * @param {Object} quoteItem - A cached quote item
 * @returns {boolean}
 */
export const hasActiveFixedPrice = (quoteItem) => {
    if (!quoteItem?.partLibraryEntryId || !quoteItem.partLibraryEntry?.fixedPrice) return false
    const { fixedPriceEndTime } = quoteItem.partLibraryEntry
    if (!fixedPriceEndTime) return true
    return new Date().setHours(0, 0, 0, 0) <= new Date(fixedPriceEndTime).setHours(0, 0, 0, 0)
}

/**
 * Reads one item's adjustment as a group key. Quote items and misc items store the
 * same idea under different field names.
 */
const readAdjustment = (isPercentage, percentage, amount, quantity) => {
    if (isPercentage) {
        return percentage ? { kind: 'percent', percent: percentage } : { kind: 'none' }
    }
    return amount ? { kind: 'fixed', amount: amount * (quantity ?? 1) } : { kind: 'none' }
}

/**
 * Describes what the adjustments on a quote's items currently are.
 *
 * The API stores adjustments per item only, so a value applied to the whole quote can't be
 * read back. This works it out from the items instead, so it stays right when items are
 * added or edited later. 0% and $0 count as no adjustment, which is what Clear leaves behind.
 * Parts with an active fixed price are left out of `itemCount` and the groups, because the
 * API skips them; `fixedPriceCount` says how many there are.
 *
 * @param {Object[]} [quoteItems] - Cached quote items
 * @param {Object[]} [miscItems] - Cached misc items
 * @returns {{ itemCount: number, fixedPriceCount: number, groups: Array<
 *   { kind: 'percent', percent: number, count: number } |
 *   { kind: 'fixed', count: number, total: number } |
 *   { kind: 'none', count: number }
 * > }} Groups of items that share an adjustment, largest first. `total` is the fixed
 *   amount across those items, multiplied by quantity and rounded to cents.
 */
export const summariseItemAdjustments = (quoteItems = [], miscItems = []) => {
    const liveQuoteItems = quoteItems.filter((item) => !item.isDeleted)
    const fixedPriceCount = liveQuoteItems.filter(hasActiveFixedPrice).length
    const adjustments = [
        ...liveQuoteItems
            .filter((item) => !hasActiveFixedPrice(item))
            .map((item) =>
                readAdjustment(
                    item.isOtherChargePercentage,
                    item.otherChargePercentage,
                    item.otherChargePrice,
                    item.quantity
                )
            ),
        ...miscItems
            .filter((item) => !item.isDeleted)
            .map((item) =>
                readAdjustment(
                    item.isPriceAdjustmentPercentage,
                    item.priceAdjustmentPercentage,
                    item.priceAdjustment,
                    item.quantity
                )
            ),
    ]

    const groups = new Map()
    adjustments.forEach(({ amount, kind, percent }) => {
        const key = kind === 'percent' ? `percent:${percent}` : kind
        const group = groups.get(key) ?? { kind, count: 0, ...(kind === 'percent' ? { percent } : {}) }
        group.count += 1
        if (kind === 'fixed') group.total = (group.total ?? 0) + amount
        groups.set(key, group)
    })

    return {
        itemCount: adjustments.length,
        fixedPriceCount,
        groups: [...groups.values()]
            .map((group) => (group.kind === 'fixed' ? { ...group, total: Math.round(group.total * 100) / 100 } : group))
            .sort((a, b) => b.count - a.count || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]),
    }
}
